import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const LinkPreviewCard = dynamic(
  () => import('molecules/SmartLink/LinkPreviewCard').then(mod => ({ default: mod.LinkPreviewCard })),
  { ssr: false },
);

const LinkPreviewWrapper = ({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) => {
  const [show, setShow] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 1024;

  const updatePosition = () => {
    if (!wrapperRef.current || !previewRef.current) {
      return;
    }

    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const previewRect = previewRef.current.getBoundingClientRect();

    const gap = 12;
    const aboveTop = triggerRect.top + window.scrollY - previewRect.height - gap + 10;
    const belowTop = triggerRect.bottom + window.scrollY + gap - 10;

    const topOffset = aboveTop < window.scrollY + 20 ? belowTop : aboveTop;
    const leftOffset = triggerRect.left + triggerRect.width / 2 + window.scrollX;

    setCoords({ top: topOffset, left: leftOffset });
  };

  const handleEnter = () => {
    if (!isDesktop()) {
      return;
    }

    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    setHasOpened(true);
    setShow(true);
  };

  const handleLeave = (e: React.MouseEvent) => {
    if (!isDesktop()) {
      return;
    }

    const related = e.relatedTarget as Node | null;
    const previewEl = document.getElementById('link-preview-card');

    if (related instanceof Node && (wrapperRef.current?.contains(related) || previewEl?.contains(related))) {
      return;
    }

    timeout.current = setTimeout(() => setShow(false), 200);
  };

  useEffect(() => {
    if (!show || !previewRef.current) {
      return;
    }

    const observer = new ResizeObserver(() => updatePosition());
    observer.observe(previewRef.current);

    updatePosition();

    return () => observer.disconnect();
  }, [show]);

  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    },
    [],
  );

  return (
    <>
      <span
        ref={wrapperRef}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        className={`relative inline-block ${className || ''}`}
      >
        {children}
      </span>

      {hasOpened &&
        createPortal(
          <div
            id="link-preview-card"
            ref={previewRef}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            className="absolute z-[9999] -translate-x-1/2 transition-opacity duration-150"
            style={{
              top: coords.top,
              left: coords.left,
              opacity: show ? 1 : 0,
              pointerEvents: show ? 'auto' : 'none',
            }}
          >
            <LinkPreviewCard href={href} />
          </div>,
          document.body,
        )}
    </>
  );
};

export default LinkPreviewWrapper;
